(function (root, factory) {
    const api = factory();
    if (typeof module === "object" && module.exports) module.exports = api;
    else root.TiorCartSync = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
    "use strict";

    function propertiesOf(value) {
        if (value == null) return {};
        if (typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid cart properties.");
        const entries = Object.keys(value).sort().filter(key => value[key] != null).map(key => {
            const text = String(value[key]);
            if (key.length > 100 || text.length > 1000) throw new Error("Cart property exceeds its limit.");
            return [key, text];
        });
        if (entries.length > 50) throw new Error("Too many cart properties.");
        return Object.fromEntries(entries);
    }

    function normalizeLine(line) {
        const variantId = String(line.variantId || line.variant_id || line.id || "");
        const allocation = line.selling_plan_allocation;
        const rawPlan = line.sellingPlan || line.selling_plan || (allocation && allocation.selling_plan && allocation.selling_plan.id);
        const sellingPlan = rawPlan ? String(rawPlan) : null;
        const quantity = Number(line.quantity);
        if (!/^\d{1,32}$/.test(variantId) || (sellingPlan && !/^\d{1,32}$/.test(sellingPlan))) throw new Error("Invalid cart identity.");
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 999) throw new Error("Invalid cart quantity.");
        return { variantId, quantity, properties: propertiesOf(line.properties), sellingPlan, selected: line.selected !== false };
    }

    function lineKey(line) {
        const normalized = normalizeLine(line);
        return JSON.stringify([normalized.variantId, Object.entries(normalized.properties), normalized.sellingPlan]);
    }

    function normalizeItems(items) {
        if (!Array.isArray(items) || items.length > 250) throw new Error("Invalid cart items.");
        const grouped = new Map();
        for (const raw of items) {
            const line = normalizeLine(raw);
            const key = lineKey(line);
            const previous = grouped.get(key);
            if (previous) {
                line.quantity += previous.quantity;
                line.selected = line.selected && previous.selected;
                if (line.quantity > 999) throw new Error("Combined cart quantity exceeds its limit.");
            }
            grouped.set(key, line);
        }
        return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([, line]) => line);
    }

    function signature(items) {
        return JSON.stringify(normalizeItems(items));
    }

    function asMap(items) {
        return new Map(normalizeItems(items).map(line => [lineKey(line), line]));
    }

    function mergeInitial(localItems, remoteItems) {
        const merged = asMap(remoteItems);
        for (const line of normalizeItems(localItems)) {
            const key = lineKey(line);
            const previous = merged.get(key);
            merged.set(key, previous ? { ...previous, quantity: Math.max(previous.quantity, line.quantity) } : line);
        }
        return normalizeItems(Array.from(merged.values()));
    }

    function rebase(baseItems, localItems, remoteItems) {
        const base = asMap(baseItems), local = asMap(localItems), result = asMap(remoteItems);
        for (const key of new Set([...base.keys(), ...local.keys()])) {
            const before = base.get(key), after = local.get(key), remote = result.get(key);
            const delta = (after ? after.quantity : 0) - (before ? before.quantity : 0);
            const quantity = Math.max(0, (remote ? remote.quantity : 0) + delta);
            if (quantity > 999) throw new Error("Rebased cart quantity exceeds its limit.");
            if (!quantity) {
                result.delete(key);
                continue;
            }
            const selectedChanged = after && (!before || after.selected !== before.selected);
            result.set(key, { ...(remote || after || before), quantity, selected: selectedChanged ? after.selected : (remote || after || before).selected });
        }
        return normalizeItems(Array.from(result.values()));
    }

    function ajaxPatch(cart, desiredItems) {
        const desired = asMap(desiredItems), grouped = new Map(), updates = {};
        for (const line of cart.items || []) {
            const key = lineKey(line);
            if (!line.key) throw new Error("Shopify cart line key is missing.");
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key).push(line);
        }
        for (const [key, lines] of grouped) {
            let delta = (desired.get(key)?.quantity || 0) - lines.reduce((sum, line) => sum + Number(line.quantity), 0);
            if (delta > 0) updates[lines[0].key] = Number(lines[0].quantity) + delta;
            if (delta < 0) {
                for (const line of [...lines].reverse()) {
                    const reduction = Math.min(-delta, Number(line.quantity));
                    if (reduction) updates[line.key] = Number(line.quantity) - reduction;
                    delta += reduction;
                }
            }
        }
        const additions = Array.from(desired).filter(([key]) => !grouped.has(key)).map(([, line]) => ({
            id: line.variantId, quantity: line.quantity, properties: line.properties,
            ...(line.sellingPlan ? { selling_plan: line.sellingPlan } : {}),
        }));
        return { updates, additions };
    }

    function createAjaxAdapter({ request, readChecked = () => ({}), writeChecked = () => {}, notify = () => {} }) {
        const read = async () => {
            const cart = await request("cart.js", { method: "GET", headers: { Accept: "application/json" } });
            if (!cart || !Array.isArray(cart.items)) throw new Error("Shopify cart response is incomplete.");
            return cart;
        };
        const snapshot = (cart, fallbackItems = []) => {
            const checked = readChecked();
            const fallback = asMap(fallbackItems);
            return normalizeItems((cart.items || []).map(line => ({ ...line, selected: Object.hasOwn(checked, line.key) ? checked[line.key] !== false : fallback.get(lineKey(line))?.selected !== false })));
        };
        const post = (path, body) => request(path, {
            method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body),
        });
        async function apply(items, observedItems, assertCurrent = () => {}) {
            let cart = await read();
            assertCurrent();
            let current = snapshot(cart), desired = rebase(observedItems, current, items), changed = false;
            for (let attempt = 0; attempt < 8; attempt += 1) {
                const patch = ajaxPatch(cart, desired);
                if (!Object.keys(patch.updates).length && !patch.additions.length) {
                    const selections = asMap(desired), checked = {};
                    for (const line of cart.items || []) checked[line.key] = selections.get(lineKey(line))?.selected !== false;
                    writeChecked(checked);
                    if (changed || signature(current) !== signature(desired)) notify(cart);
                    return cart;
                }
                let expected;
                assertCurrent();
                if (Object.keys(patch.updates).length) {
                    const predicted = (cart.items || []).map(line => ({ ...line, quantity: Object.hasOwn(patch.updates, line.key) ? patch.updates[line.key] : line.quantity })).filter(line => line.quantity > 0);
                    expected = snapshot({ items: predicted });
                    await post("cart/update.js", { updates: patch.updates });
                } else {
                    expected = normalizeItems([...current, ...patch.additions.map(line => ({ ...line, selected: asMap(desired).get(lineKey(line))?.selected !== false }))]);
                    await post("cart/add.js", { items: patch.additions });
                }
                assertCurrent();
                changed = true;
                cart = await read();
                assertCurrent();
                // A newly-added Shopify key has no checkbox entry yet. Retain the
                // acknowledged selection instead of treating that as a user edit.
                current = snapshot(cart, expected);
                desired = rebase(expected, current, desired);
            }
            throw new Error("Cart kept changing during synchronization. Retry later.");
        }
        return { read, snapshot, apply };
    }

    function createEngine({ customerId, readRemote, writeRemote, local, readState, writeState, withLock = task => task() }) {
        let inFlight = null;
        const reconciliationRequired = () => {
            const error = new Error("Cart synchronization needs reconciliation before another write.");
            error.code = "CART_SYNC_RECONCILIATION_REQUIRED";
            return error;
        };
        const cleanState = (owner, remote) => ({ version: 2, customerId: owner, serverUpdatedAt: remote.updatedAt || null, items: normalizeItems(remote.items || []) });
        async function finishLocal(owner, remote, observedItems, assertCurrent) {
            writeState({ ...cleanState(owner, remote), pending: { phase: "local", observedItems, targetItems: normalizeItems(remote.items || []) } });
            const applied = await local.apply(normalizeItems(remote.items || []), observedItems, assertCurrent);
            assertCurrent();
            writeState(cleanState(owner, remote));
            return applied;
        }
        async function run() {
            const owner = String(customerId() || "");
            if (!owner) return null;
            const assertCurrent = () => {
                if (String(customerId() || "") !== owner) throw new Error("Cart customer changed during synchronization.");
            };
            const assertRemote = remote => {
                assertCurrent();
                if (!remote || !Array.isArray(remote.items) || (remote.customerId != null && String(remote.customerId) !== owner)) throw new Error("Cart snapshot customer or items are invalid.");
                normalizeItems(remote.items);
            };
            let state = readState();
            if (state && state.customerId === owner && state.pending) {
                let pending = state.pending;
                if (pending.phase === "remote") {
                    const acknowledged = await writeRemote(pending.request);
                    assertRemote(acknowledged);
                    if (acknowledged.conflict && signature(acknowledged.items || []) !== signature(pending.request.items)) throw reconciliationRequired();
                    state = { ...cleanState(owner, acknowledged), pending: { phase: "local", observedItems: pending.observedItems, targetItems: normalizeItems(acknowledged.items || []) } };
                    writeState(state);
                    pending = state.pending;
                }
                const current = await local.read();
                assertCurrent();
                const currentItems = local.snapshot(current);
                if (signature(currentItems) === signature(pending.targetItems)) {
                    writeState({ ...state, pending: undefined });
                } else if (signature(currentItems) === signature(pending.observedItems)) {
                    await finishLocal(owner, { items: pending.targetItems, updatedAt: state.serverUpdatedAt }, pending.observedItems, assertCurrent);
                } else {
                    throw reconciliationRequired();
                }
            }
            const cart = await local.read();
            assertCurrent();
            const localItems = local.snapshot(cart);
            let remote = await readRemote();
            assertRemote(remote);
            state = readState();
            const base = state && state.version === 2 && state.customerId === owner && Array.isArray(state.items) ? state.items : null;
            let saved = false;
            for (let attempt = 0; attempt < 4; attempt += 1) {
                const remoteItems = normalizeItems(remote.items || []);
                const desired = base === null ? mergeInitial(localItems, remoteItems) : rebase(base, localItems, remoteItems);
                if (signature(desired) === signature(remoteItems)) { saved = true; break; }
                assertCurrent();
                const request = { items: desired, baseUpdatedAt: remote.updatedAt || null, mode: base === null ? "merge" : "replace" };
                writeState({ ...(state || {}), version: 2, customerId: owner, pending: { phase: "remote", request, observedItems: localItems } });
                remote = await writeRemote(request);
                assertRemote(remote);
                if (!remote.conflict) { saved = true; break; }
                writeState(state);
            }
            if (!saved) throw new Error("Cart revision changed repeatedly. Retry later.");
            return finishLocal(owner, remote, localItems, assertCurrent);
        }
        function sync() {
            if (!inFlight) inFlight = Promise.resolve().then(() => withLock(run)).finally(() => { inFlight = null; });
            return inFlight;
        }
        return { sync };
    }

    return { propertiesOf, normalizeLine, normalizeItems, lineKey, signature, mergeInitial, rebase, ajaxPatch, createAjaxAdapter, createEngine };
});
